import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";

import {
  getRefundReasonLabel,
  REFUND_REASONS_OPTIONS,
} from "@/constants/retailProducts";
import {
  ImageUploaderField,
  SelectField,
  SimpleFormProvider,
  TextAreaField,
  ToggleButtonField,
} from "@/form";
import { Text } from "atoms/Text/Text";

import { RetailProductOrderReturnLineItemCard } from "../RetailProductOrderReturnLineItemCard/RetailProductOrderReturnLineItemCard";

const returnReasonsSchema = z
  .object({
    hasBeenOpenedOrUsed: z.boolean({
      error: "Please indicate if the item has been opened or used",
    }),
    isProductDefective: z.boolean({
      error: "Please indicate if the product is defective",
    }),
    refundReason: z.number().optional(),
    additionalInfo: z.string(),
    imageUrls: z.array(
      z.string({
        error: "Please select at least one image",
      })
    ),
  })
  .transform((data) => ({
    ...data,
    refundReason: data.refundReason
      ? getRefundReasonLabel(data.refundReason)
      : undefined,
  }));

export type ReturnReasonsFormData = z.infer<typeof returnReasonsSchema>;

const QuestionWrapper = ({
  children,
  shouldShow,
}: {
  children: React.ReactNode;
  shouldShow: boolean;
}) => {
  return (
    <div
      className={`flex w-full flex-col gap-2 transition-all duration-500 ease-in-out ${
        shouldShow
          ? "max-h-screen opacity-100"
          : "pointer-events-none max-h-0 overflow-hidden opacity-0"
      }`}
    >
      {children}
    </div>
  );
};

const ItemHasBeenOpenedOrUsed = () => {
  return (
    <QuestionWrapper shouldShow={true}>
      <div className="flex w-full flex-col">
        <Text variant="body2">Has the item been opened or used?</Text>
        <ToggleButtonField
          name="hasBeenOpenedOrUsed"
          variant="v3"
          trueLabel="Yes"
          falseLabel="No"
        />
      </div>
    </QuestionWrapper>
  );
};

const IsTheProductDamagedOrDefective = () => {
  const { watch } = useFormContext<ReturnReasonsFormData>();
  const hasBeenOpenedOrUsed = watch("hasBeenOpenedOrUsed");
  const isProductDefective = watch("isProductDefective");

  return (
    <QuestionWrapper shouldShow={hasBeenOpenedOrUsed !== undefined}>
      <div className="flex w-full flex-col">
        <Text variant="body2">Is the product damaged or defective?</Text>
        <ToggleButtonField
          name="isProductDefective"
          variant="v3"
          trueLabel="Yes"
          falseLabel="No"
        />
        {isProductDefective === false && hasBeenOpenedOrUsed === true && (
          <div className="flex flex-col gap-2 transition-all duration-500 ease-in-out animate-in fade-in slide-in-from-top-2">
            <Text variant="body2">
              Unfortunately and without exception, we cannot accept returns on
              used or opened items - as we guarantee the purity, freshness, and
              authenticity of the products we sell.
            </Text>
          </div>
        )}
      </div>
    </QuestionWrapper>
  );
};

const RefundReason = () => {
  const { watch } = useFormContext<ReturnReasonsFormData>();
  const hasBeenOpenedOrUsed = watch("hasBeenOpenedOrUsed");
  const isProductDefective = watch("isProductDefective");

  const shouldShowRefundReason = useMemo(() => {
    // Show refund reason unless: item has been opened/used AND product is NOT defective
    return !(hasBeenOpenedOrUsed === true && isProductDefective === false);
  }, [hasBeenOpenedOrUsed, isProductDefective]);

  return (
    <QuestionWrapper shouldShow={shouldShowRefundReason}>
      <SelectField
        name="refundReason"
        options={REFUND_REASONS_OPTIONS}
        placeholder="Select reason for return/refund"
        variant="v3"
        valueOnly
      />
    </QuestionWrapper>
  );
};

const AdditionalInfoForDefectiveProduct = () => {
  const { watch } = useFormContext<ReturnReasonsFormData>();
  const refundReason = watch("refundReason");

  return (
    <QuestionWrapper shouldShow={refundReason !== undefined}>
      <div className="mt-6 flex w-full flex-col">
        <Text variant="body2">
          Add any additional info that will help us assist you in your
          return/refund
        </Text>
        <TextAreaField
          name="additionalInfo"
          label=""
          placeholder="Please provide any additional information..."
        />
      </div>
    </QuestionWrapper>
  );
};

const MediaUploader = () => {
  const { watch } = useFormContext<ReturnReasonsFormData>();
  const additionalInfo = watch("additionalInfo");
  const isProductDefective = watch("isProductDefective");

  return (
    <QuestionWrapper
      shouldShow={!!additionalInfo && isProductDefective === true}
    >
      <div className="mt-6 flex w-full flex-col gap-4">
        <Text variant="body2">
          Please attach a photo of the product.{" "}
          <Text variant="bold2">
            This must include an image of the lot number.
          </Text>
        </Text>
        <div className="transition-all duration-500 ease-in-out animate-in fade-in slide-in-from-top-2">
          <ImageUploaderField multiple name="imageUrls" label="" hint="" />
        </div>
      </div>
    </QuestionWrapper>
  );
};

const QuestionsContainer = () => {
  return (
    <div className="flex flex-col">
      <ItemHasBeenOpenedOrUsed />
      <IsTheProductDamagedOrDefective />
      <RefundReason />
      <AdditionalInfoForDefectiveProduct />
      <MediaUploader />
    </div>
  );
};

export interface ReturnReasonsAndMediaProps {
  selectedItem?: any;
  defaultValues?: Partial<ReturnReasonsFormData>;
  onSubmit?: (data: ReturnReasonsFormData) => Promise<unknown>;
  onSuccess?: (data: ReturnReasonsFormData, response: unknown) => void;
  onChange?: (data: ReturnReasonsFormData) => void;
  onError?: (error: any) => void;
}

export const ReturnReasonsAndMedia = ({
  selectedItem,
  defaultValues = {},
  onSubmit,
  onSuccess,
  onChange,
  onError,
}: ReturnReasonsAndMediaProps) => {
  const handleSubmit = useCallback(
    async (data: ReturnReasonsFormData) => {
      if (onSubmit) {
        return await onSubmit(data);
      }
      return Promise.resolve();
    },
    [onSubmit]
  );

  const handleSuccess = useCallback(
    (data: ReturnReasonsFormData, response: unknown) => {
      onSuccess?.(data, response);
    },
    [onSuccess]
  );

  const handleChange = useCallback(
    (data: ReturnReasonsFormData) => {
      onChange?.(data);
    },
    [onChange]
  );

  const handleError = useCallback(
    (error: any) => {
      onError?.(error);
    },
    [onError]
  );

  const memoizedDefaultValues = useMemo(
    () => ({
      ...defaultValues,
      imageUrls: defaultValues.imageUrls || [],
    }),
    [JSON.stringify(defaultValues)]
  );

  return (
    <SimpleFormProvider
      schema={returnReasonsSchema}
      defaultValues={memoizedDefaultValues}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onChange={handleChange}
      onError={handleError}
      hideLoading={true}
    >
      <div className="flex flex-col p-4">
        <div className="mb-4">
          <RetailProductOrderReturnLineItemCard
            hideRadio
            thumbnailImageUri={selectedItem?.thumbnailImageUri}
            name={selectedItem?.name.text}
            packageId={selectedItem?.packageId}
          />
        </div>

        <QuestionsContainer />
      </div>
    </SimpleFormProvider>
  );
};
